(ns ui.instagram-connect.account-modal
  "Replicant modal showing Instagram account details."
  (:require [storrito.ui.button :as button]
            [storrito.date.format :as f]
            [clojure.string :as str]
            [parts.replicant.query :as query]
            [parts.replicant.command :as command]
            [ui.instagram-connect.page :as page]
            [ui.totp.generate :as totp-gen]
            [app.totp.validation :as validation]
            [storrito.instagram-connect.disconnect-reason :as reason]))

(defn profile-header
  "Top section: profile picture with IG gradient ring, username, badges, sync button."
  [state instagram-user]
  (let [user-name (:instagram-user/name instagram-user)
        blob-id (:instagram.user/profil-picture-blob-id instagram-user)
        img-src (if blob-id
                  (str "/image/" blob-id)
                  "/img/anonymous.png")
        is-business? (:instagram.user/is-business? instagram-user)
        connected? (not (:instagram.account/not-connected instagram-user))
        syncing? (::syncing state)]
    [:div.flex.items-center.gap-5.pb-5.border-b.border-gray-200
     [:div.shrink-0
      [:div.rounded-full.p-0.5
       {:class ["bg-gradient-to-tr" "from-amber-400" "via-pink-500" "to-purple-600"]}
       [:a.block.bg-white.rounded-full.p-0.5
        {:href (str "https://www.instagram.com/stories/" user-name "/")
         :target "_blank"
         :title "View stories"}
        [:img.rounded-full.h-20.w-20
         {:src img-src
          :alt user-name}]]]]
     [:div.flex.flex-col.gap-1.min-w-0
      [:div.flex.items-center.gap-2.flex-wrap
       [:h2.text-xl.font-bold.text-gray-900.truncate
        user-name]
       [:button.inline-flex.items-center.px-2.py-0.5.rounded-full.text-xs.font-medium.bg-gray-100.text-gray-600.hover:bg-gray-200.border.border-gray-200.transition-colors
        {:disabled syncing?
         :on {:click [[:ig-account-modal/sync
                       (:instagram.user/id instagram-user)]]}}
        (if syncing?
          [:i.fas.fa-spinner.fa-spin.mr-1]
          [:i.fas.fa-sync.mr-1])
        "Sync"]
       (when is-business?
         [:span.inline-flex.items-center.px-2.py-0.5.rounded-full.text-xs.font-medium.bg-blue-50.text-blue-700.border.border-blue-200
          [:i.fas.fa-briefcase.mr-1]
          "Business"])
       (if connected?
         [:span.inline-flex.items-center.px-2.py-0.5.rounded-full.text-xs.font-medium.bg-green-50.text-green-700.border.border-green-200
          [:i.fas.fa-check-circle.mr-1]
          "Connected"]
         [:span.inline-flex.items-center.px-2.py-0.5.rounded-full.text-xs.font-medium.bg-red-50.text-red-700.border.border-red-200
          [:i.fas.fa-circle-exclamation.mr-1]
          "Disconnected"])]
      [:a.text-sm.text-gray-500.hover:text-gray-700
       {:href (str "https://www.instagram.com/" user-name "/")
        :target "_blank"}
       [:i.fas.fa-external-link-alt.mr-1]
       "instagram.com/" user-name]]]))

(defn facebook-section
  "Facebook linkages display."
  [instagram-user]
  (let [linkages (:instagram.user/facebook-linkages instagram-user)]
    [:div.py-4.border-b.border-gray-200
     [:h3.text-xs.font-semibold.text-gray-400.uppercase.tracking-wider.mb-3
      "Facebook"]
     (if (seq linkages)
       [:div.flex.flex-col.gap-2
        (map
          (fn [{:keys [facebook-linkage/name facebook-linkage/id facebook-linkage/type]}]
            [:div.flex.items-center.gap-2.text-sm
             [:i.fab.fa-facebook.text-blue-500]
             [:a.text-gray-700.hover:text-gray-900.hover:underline
              {:href (str "https://www.facebook.com/" id)
               :target "_blank"}
              name]
             [:span.text-gray-400.text-xs
              (str "(" type ")")]])
          linkages)]
       [:div.flex.items-center.gap-2.text-sm.text-gray-500
        [:i.fab.fa-facebook.text-blue-400]
        "Facebook stories available"])]))

(defn connection-detail
  [label value]
  [:div.flex.items-center.justify-between.py-1.5
   [:span.text-xs.text-gray-400 label]
   [:span.text-sm.text-gray-700 value]])

(defn proxy-detail-value
  [proxy-data]
  (let [proxy-ident (:ident proxy-data)
        relay-app? (= :proxy.kind/storrito-relay-app (:kind proxy-data))]
    (if relay-app?
      [:span.inline-flex.max-w-48.items-center.gap-1.rounded-full.border.border-blue-200.bg-blue-50.px-2.py-0.5.text-blue-700.align-bottom
       {:title (or (:url proxy-data) proxy-ident)}
       [:i.fas.fa-plug.text-xs]
       [:span.truncate (or proxy-ident "Storrito Relay App")]]
      [:span.truncate.max-w-48.inline-block.align-bottom
       {:title proxy-ident}
       (or proxy-ident "-")])))

(defn disconnect-reason-card
  "Shows disconnect reason, description, recommendation and reconnect button."
  [instagram-user]
  (let [disconnect-reason (:instagram.account/disconnect-reason instagram-user)
        matched (reason/match-reason disconnect-reason)]
    (when matched
      (let [recommendation-text (reason/get-recommendation-text disconnect-reason)
            user-name (:instagram-user/name instagram-user)]
        [:div.rounded-lg.border.border-red-200.bg-red-50.p-4.flex-1
         [:div.flex.items-start.gap-3
          [:div.shrink-0.h-8.w-8.rounded-full.bg-red-100.flex.items-center.justify-center
           [:i.fas.fa-circle-exclamation.text-red-600.text-sm]]
          [:div.flex.flex-col.gap-2.min-w-0
           [:div
            [:span.text-sm.font-semibold.text-red-800
             (:label matched)]
            [:p.text-sm.text-red-700.mt-0.5
             (:description matched)]]
           (when recommendation-text
             [:div.rounded-md.bg-white.border.border-red-100.p-3
              [:div.flex.items-start.gap-2
               [:i.fas.fa-lightbulb.text-amber-500.mt-0.5]
               [:p.text-sm.text-gray-700 recommendation-text]]])
           [:a {:href (str "/ui/connect3/" (random-uuid)
                           "?username=" user-name
                           "&reconnect=" (:instagram.account/reconnect instagram-user))
                :target "_blank"
                :on {:click [[:ui/modal-close]]}
                :class (concat button/css-default button/css-sm button/css-primary)}
            [:i.fas.fa-plug.mr-1]
            "Reconnect"]]]]))))

(defn connections-section
  "Connection details display."
  [instagram-user]
  (let [disconnected? (:instagram.account/not-connected instagram-user)
        connections (:instagram.account/active-connections instagram-user)
        connection (first
                     (reverse
                       (sort-by :instagram.account/last-successful-connect
                                connections)))]
    [:div.flex.flex-col
     [:h3.text-xs.font-semibold.text-gray-400.uppercase.tracking-wider.mb-3
      "Connection"]
     (if disconnected?
       (or (disconnect-reason-card instagram-user)
           [:div.rounded-lg.border.border-dashed.border-gray-300.bg-gray-50.p-4.flex-1
            [:p.text-sm.text-gray-500 "Disconnected"]])
       (when connection
         (let [proxy-data (:instagram.account/proxy connection)
               device (:instagram.account/device connection)
               last-connect (f/format f/date-formatter
                                      (:instagram.account/last-successful-connect connection))]
           [:div.rounded-lg.border.border-gray-200.bg-white.p-4.divide-y.divide-gray-100.flex-1
            (connection-detail "Last connected" (or last-connect "-"))
            (connection-detail "Device"
                               [:span.truncate.max-w-48.inline-block.align-bottom
                                {:title device}
                                (or device "unknown")])
            (connection-detail "Proxy"
                               (proxy-detail-value proxy-data))
            (connection-detail "Version"
                               (or (:instagram.account/version connection) "unknown"))])))]))

(defn otp-preview
  "Shows a live OTP code generated client-side from the entered secret.
   Allows the user to copy the code and verify it in Instagram."
  [preview-otp preview-progress]
  [:div.mt-3.rounded-lg.border.border-green-200.bg-green-50.p-3
   [:div.flex.items-center.justify-between
    [:div.flex.items-center.gap-2
     [:div.h-6.w-6.rounded-full.bg-green-100.flex.items-center.justify-center
      [:i.fas.fa-check.text-green-600.text-xs]]
     [:span.text-xs.font-medium.text-green-700 "Verification code"]]
    [:div.flex.items-center.gap-2
     [:span.text-2xl.font-mono.font-bold.tracking-widest.text-gray-900
      (subs preview-otp 0 3) [:span.text-gray-300.mx-0.5 " "] (subs preview-otp 3)]
     [:button.h-7.w-7.rounded-full.bg-white.border.border-green-200.hover:bg-green-100.flex.items-center.justify-center.text-green-600.hover:text-green-700.transition-colors
      {:on {:click [[:ig-account-modal/copy-otp preview-otp]]}
       :title "Copy code"}
      [:i.fas.fa-copy.text-xs]]]]
   [:div.mt-2
    [:div.h-1.w-full.bg-green-100.rounded-full.overflow-hidden
     [:div.h-full.rounded-full.transition-all
      {:class (if (< preview-progress 0.2) "bg-red-400" "bg-green-500")
       :style {:width (str (* 100 preview-progress) "%")}}]]]
   [:p.text-xs.text-green-600.mt-2
    [:i.fas.fa-arrow-right.mr-1]
    "Enter this code in Instagram, then click "
    [:span.font-semibold "Confirmed"]]])

(defn totp-section
  "2FA/TOTP status and OTP code display."
  [state instagram-user]
  (let [has-totp? (:instagram.user/totp instagram-user)
        otp (:totp/otp instagram-user)
        progress (page/calc-expire-progress state)]
    [:div.flex.flex-col
     [:h3.text-xs.font-semibold.text-gray-400.uppercase.tracking-wider.mb-3
      "Two-Factor Authentication"]
     (if has-totp?
       (let [totp-uuid (get-in instagram-user [:instagram.user/totp :totp/uuid])
             delete-command {:command/kind :command/totp-delete
                             :command/data {:totp/uuid (str totp-uuid)}}]
         [:div.rounded-lg.border.border-gray-200.bg-white.p-4.flex-1
          [:div.flex.items-center.justify-between
           [:div.flex.items-center.gap-2
            [:div.h-8.w-8.rounded-full.bg-green-50.flex.items-center.justify-center
             [:i.fas.fa-shield-halved.text-green-600.text-sm]]
            [:span.text-sm.font-medium.text-gray-700 "TOTP Active"]]
           (when otp
             [:div.flex.items-center.gap-3
              [:span.text-2xl.font-mono.font-bold.tracking-widest.text-gray-900
               (subs otp 0 3) [:span.text-gray-300.mx-0.5 " "] (subs otp 3)]
              [:button.h-8.w-8.rounded-full.bg-gray-100.hover:bg-gray-200.flex.items-center.justify-center.text-gray-500.hover:text-gray-700.transition-colors
               {:on {:click [[:ig-account-modal/copy-otp otp]]}
                :title "Copy code"}
               [:i.fas.fa-copy.text-xs]]])]
          (when otp
            [:div.mt-3
             [:div.h-1.w-full.bg-gray-100.rounded-full.overflow-hidden
              [:div.h-full.rounded-full.transition-all
               {:class (if (< progress 0.2) "bg-red-400" "bg-indigo-500")
                :style {:width (str (* 100 progress) "%")}}]]])
          [:div.mt-3.flex.justify-end
           [:button.text-xs.text-red-400.hover:text-red-600.transition-colors
            {:disabled (command/issued? state delete-command)
             :on {:click [[:data/command
                           delete-command
                           {:on-success [[:data/query page/ig-connect-query]]
                            :on-error []}]]}}
            [:i.fas.fa-trash-can.mr-1]
            "Remove"]]])
       (let [raw-input (or (::totp-secret-input state) "")
             totp-uuid (or (::totp-uuid state)
                           (str (random-uuid)))
             secret-key (validation/clean-secret raw-input)
             char-count (count secret-key)
             has-input? (pos? char-count)
             valid-chars? (or (empty? secret-key)
                              (validation/valid-base32? secret-key))
             valid? (validation/valid-secret-format? secret-key)
             preview-otp (when valid? (totp-gen/generate-otp secret-key))
             preview-progress (when preview-otp
                                (let [clock (or (:ui/clock state) (.now js/Date))]
                                  (/ (totp-gen/seconds-remaining clock) totp-gen/totp-period)))
             error-message (when has-input?
                             (cond
                               (not valid-chars?)
                               "Only base32 characters allowed (A-Z, 2-7)"

                               (and (not valid?) (>= char-count validation/secret-key-length))
                               (str "Too long (" char-count " chars, need exactly " validation/secret-key-length ")")

                               :else nil))
             save-command {:command/kind :command/totp-add-and-link
                           :command/data {:totp/uuid totp-uuid
                                          :totp/name (str "2FA " (:instagram-user/name instagram-user))
                                          :totp/secret-key secret-key
                                          :instagram.user/id (:instagram.user/id instagram-user)}}
             server-error (command/get-latest-error state save-command)]
         [:div.rounded-lg.border.border-dashed.border-gray-300.bg-gray-50.p-4.flex-1
          [:div.flex.items-center.gap-2.mb-3
           [:div.h-8.w-8.rounded-full.bg-gray-100.flex.items-center.justify-center
            [:i.fas.fa-shield-halved.text-gray-400.text-sm]]
           [:span.text-sm.text-gray-500 "No authenticator configured"]]
          [:div.flex.items-center.gap-2
           [:input.flex-1.px-3.py-1.5.border.rounded-md.text-sm.font-mono
            {:class (concat ["focus:ring-1" "placeholder:text-gray-400"]
                            (if (and has-input? (not valid-chars?))
                              ["border-red-300" "focus:ring-red-500" "focus:border-red-500"]
                              ["border-gray-300" "focus:ring-indigo-500" "focus:border-indigo-500"]))
             :type "text"
             :placeholder "Paste TOTP secret key (32 characters)"
             :value raw-input
             :disabled (command/issued? state save-command)
             :on {:input [[:store/assoc ::totp-secret-input :event/target.value]
                          [:store/assoc ::totp-uuid totp-uuid]]}}]
           [:button
            {:class (concat button/css-default button/css-sm button/css-primary)
             :disabled (or (command/issued? state save-command)
                           (not valid?))
             :on {:click [[:data/command
                           save-command
                           {:on-success [[:data/query page/ig-connect-query]
                                         [:store/dissoc ::totp-secret-input]
                                         [:store/dissoc ::totp-uuid]
                                         [:ui/modal-close]]
                            :on-error []}]]}}
            (if preview-otp
              [:span [:i.fas.fa-check.mr-1] "Confirmed"]
              "Save")]]
          (when preview-otp
            (otp-preview preview-otp preview-progress))
          (when (and has-input? (not preview-otp))
            [:div.mt-2.flex.items-center.justify-between
             [:div.text-xs
              (if error-message
                [:span.text-red-500 error-message]
                [:span.text-gray-400
                 (str char-count "/" validation/secret-key-length " characters")])]
             (when server-error
               [:span.text-xs.text-red-500 server-error])])]))]))


(defn find-user-by-name
  [state username]
  (let [result (query/get-result state page/ig-connect-query)
        instagram-users (:instagram-users result)]
    (first (filter #(= (:instagram-user/name %) username)
                   instagram-users))))

(def delete-question
  "Delete this Instagram connection? Storrito will not be able to post scheduled Stories for this account.")

(defn delete-account-footer
  "The Delete Account button; after a click the question it used to ask
   through `js/confirm`, with Cancel and a confirm button in the modal.
   A native dialog is not answerable by every client: a customer's
   browser-control session hung on it in two browsers and the delete
   request never left the page (brain-129). `::deleting?` and
   `::delete-error` are set by `:ig-account-modal/delete-account`."
  [state instagram-user]
  (let [deleting? (::deleting? state)]
    (if (::confirm-delete? state)
      [:div.flex.flex-col.gap-2
       [:span.text-sm.text-gray-700
        delete-question]
       (when-let [error (::delete-error state)]
         [:span.text-sm.text-red-600
          error])
       [:div.flex.gap-2
        [:button
         {:class button/button-cancel-css
          :type "button"
          :disabled deleting?
          :on {:click [[:store/assoc-in [::confirm-delete?] false]]}}
         "Cancel"]
        [:button.inline-flex.items-center.rounded-md.bg-red-600.px-3.py-2.text-sm.font-semibold.text-white.shadow-sm.hover:bg-red-500
         {:type "button"
          :disabled deleting?
          :on {:click [[:ig-account-modal/delete-account
                        (:instagram.user/id instagram-user)]]}}
         (if deleting?
           [:i.fas.fa-spinner.fa-spin.mr-1]
           [:i.fas.fa-trash-can.mr-1])
         "Yes, delete the connection"]]]
      [:button.text-sm.text-red-400.hover:text-red-600.transition-colors
       {:type "button"
        :on {:click [[:store/assoc-in [::confirm-delete?] true]]}}
       [:i.fas.fa-trash-can.mr-1]
       "Delete Account"])))

(defn render
  [state]
  (let [username (get-in state [:ui/modal :params :username])
        instagram-user (find-user-by-name state username)]
    (when instagram-user
      [:div
       (profile-header state instagram-user)
       (facebook-section instagram-user)
       [:div.grid.gap-4.pt-4
        {:class ["md:grid-cols-2"]}
        (connections-section instagram-user)
        (totp-section state instagram-user)]
       [:div.flex.justify-between.items-start.gap-4.pt-4.mt-4.border-t.border-gray-200
        (delete-account-footer state instagram-user)
        [:button.text-sm.text-gray-500.hover:text-gray-700.transition-colors
         {:type "button"
          :on {:click [[:ui/modal-close]]}}
         "Close"]]])))

(def modal
  {:modal-id :modals/instagram-account
   :on-load (fn [state]
              (let [username (get-in state [:ui/modal :params :username])
                    instagram-user (find-user-by-name state username)]
                (into [[:store/assoc-in [::confirm-delete?] false]
                       [:store/assoc-in [::delete-error] nil]]
                      (when (:instagram.user/totp instagram-user)
                        [[:data/query {:query/kind :query/totp-otp
                                       :instagram.user/id (:instagram.user/id instagram-user)}]]))))
   :render #'render})
